import React from 'react';
import styled, { css } from 'styled-components';
import { Button } from 'src/common/components/Button';
import { typography } from 'src/config/typography';
import { colors } from 'src/config/colors';
import { useDispatch, useSelector } from 'react-redux';
import {
  courseListingQueryFiltersSelector,
  courseListingViewType,
} from 'src/state/courseListing/courseListingSelectors';
import { CourseViewType } from 'src/state/courseListing/courseListingReducer';
import { courseListingActions } from 'src/state/courseListing/courseListingActions';
import {
  courseViewTypeInfoMapper,
  courseViewTypes,
} from 'src/public/components/course-listing/course-listing-config';
import { media } from 'src/config/breakpoints';
import OverlayScrollbars from 'overlayscrollbars';
import { ScrollContainer } from 'src/common/components/ScrollContainer';
import { useOnlineCourseList } from 'src/common/hooks/useOnlineCourseList';
import { useElearningList } from 'src/common/hooks/useElearningList';
import { firstPageNmb, firstRequestSize } from 'src/config/listing';
import { useCourseBundleList } from 'src/common/hooks/useCourseBundleList';

const Container = styled.div`
  margin-bottom: 0.75rem;

  ${media.w.between.px768.px992(css`
    margin-left: 1.5rem;
  `)}

  ${media.w.min.px768(css`
    margin-bottom: 0;
  `)}
`;

const ToggleContainer = styled.div`
  align-items: center;
  border: 0.0625rem solid #a8abb0;
  border-radius: 0.375rem;
  display: flex;
  margin: 0.0625rem 0.0625rem 1.75rem;
  padding: 0.125rem;
  width: fit-content;

  ${media.w.min.px768(css`
    margin: 0.0625rem;
  `)}
`;

const Separator = styled.div`
  background: #a8abb0;
  height: 1rem;
  width: 0.0625rem;
`;

const ButtonContainer = styled.div`
  align-items: center;
  display: flex;
`;

const StyledToggleButton = styled(Button)<{ active: boolean }>`
  font-family: ${typography.fontFamilyDefault};
  font-weight: 400;
  height: 1.875rem;
  font-size: 0.75rem;
  line-height: 1rem;
  position: relative;
  display: flex;
  align-items: center;

  ${({ active }) =>
    !active &&
    css`
      background: ${colors.white};
      color: ${colors.blue};
    `}
`;

const scrollbarOptions: OverlayScrollbars.Options = {
  overflowBehavior: { y: 'hidden' },
};

export const CourseListingToggleCourse = () => {
  const dispatch = useDispatch();
  const filters = useSelector(courseListingQueryFiltersSelector);
  const selectedViewType = useSelector(courseListingViewType);

  const { data: onlineItems } = useOnlineCourseList(filters, firstPageNmb, firstRequestSize);
  const { data: elearningItems } = useElearningList(filters, firstPageNmb, firstRequestSize);
  const { data: courseBundleItems } = useCourseBundleList(filters, firstPageNmb, firstRequestSize);

  const totalElearning = elearningItems?.totalResult || 0;
  const totalOnline = onlineItems?.totalResult || 0;
  const totalCourseBundles = courseBundleItems?.totalResult || 0;

  const itemsCount = {
    [CourseViewType.Online]: totalOnline,
    [CourseViewType.Elearning]: totalElearning,
    [CourseViewType.CourseBundle]: totalCourseBundles,
    [CourseViewType.All]: totalOnline + totalElearning + totalCourseBundles,
  };

  const handleClick = (viewType: CourseViewType) => {
    dispatch(courseListingActions.toggleCourseTypeView(viewType));
  };

  return (
    <Container>
      <ScrollContainer options={scrollbarOptions}>
        <ToggleContainer>
          {courseViewTypes.map((courseViewType, i, arr) => (
            <ButtonContainer key={i}>
              <StyledToggleButton
                active={courseViewType === selectedViewType}
                kind="secondary"
                onClick={() => handleClick(courseViewType)}
              >
                {courseViewTypeInfoMapper[courseViewType].title} [{itemsCount[courseViewType]}]
              </StyledToggleButton>
              {arr[i + 1] &&
                courseViewTypeInfoMapper[arr[i + 1]].courseType !== selectedViewType &&
                courseViewType !== selectedViewType && <Separator />}
            </ButtonContainer>
          ))}
        </ToggleContainer>
      </ScrollContainer>
    </Container>
  );
};
